import React, { useEffect, useMemo, useState } from 'react';
import {
  Edit,
  CalendarPlus,
  AlertTriangle,
  Share2,
  X,
} from 'lucide-react';
import ProgressBar from '../ui/ProgressBar';
import TablePagination from '../ui/TablePagination';
import EmptyState from '../ui/EmptyState';
import Panel from '../ui/Panel';
import Avatar from '../Avatar';
import {
  isResourceBillable,
  displayName,
  formatDate,
  daysUntil,
  utilizationStatusTextClass,
} from './utilizationHelpers';

const PER_PAGE = 10;
const CHIPS_VISIBLE = 3;

/** Columns the "Columns" menu can toggle. `locked` columns are always shown. */
export const PEOPLE_COLUMNS = [
  { key: 'employee', label: 'Employee', locked: true },
  { key: 'cohort', label: 'Cohort' },
  { key: 'utilization', label: 'Utilization' },
  { key: 'projects', label: 'Projects' },
  { key: 'billability', label: 'Billability' },
  { key: 'comments', label: 'Comments' },
  { key: 'freeDate', label: 'Free date' },
  { key: 'actions', label: 'Actions' },
];

const TH_BASE =
  'px-4 py-3 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider';
const TH_CLASS = `${TH_BASE} text-left`;

/** Keep only project entries where the employee has actually clocked time. */
const activeProjectEntries = (entries = []) =>
  entries.filter((e) => Number(e?.week_hours || 0) > 0);

const ConflictChip = ({ shared, over }) => {
  if (!shared && !over) return null;
  const label = over ? 'Over-allocated' : 'Shared';
  const tone = over
    ? 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-200'
    : 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200';
  const Icon = over ? AlertTriangle : Share2;
  return (
    <span
      data-testid={over ? 'conflict-badge' : 'shared-badge'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

const BillableChip = ({ billable, manual = false }) => (
  <span
    data-testid={billable ? 'billable-chip' : 'non-billable-chip'}
    title={
      manual
        ? 'Set manually in Edit Resource'
        : 'Based on billable hours in the selected range'
    }
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
      billable
        ? 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-200'
        : 'bg-secondary-100 text-secondary-600 dark:bg-secondary-700 dark:text-secondary-300'
    }`}
  >
    {billable ? 'Billable' : 'Non-billable'}
  </span>
);

const PACE_BADGE_CONFIG = {
  on_pace: {
    text: 'On pace',
    tone: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
  ahead: {
    text: 'Ahead',
    tone: 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
  },
  behind: {
    text: 'Behind pace',
    tone: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  },
};

/**
 * Small pill signaling where the user sits vs. the elapsed working-day target
 * for the current week. Only rendered when the backend returns pace metadata
 * (``utilization.basis === 'pace_to_date'``).
 */
const PaceBadge = ({ label, tooltip }) => {
  const cfg = PACE_BADGE_CONFIG[label];
  if (!cfg) return null;
  return (
    <span
      data-testid={`pace-badge-${label}`}
      title={tooltip || ''}
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${cfg.tone}`}
    >
      {cfg.text}
    </span>
  );
};

/**
 * Renders the Utilization progress bar + hours line, and - when the backend
 * returned pace-to-date metadata - a tiny "On pace / Ahead / Behind pace"
 * badge plus a secondary "N.Nh / 40.0h this week" subtitle so both numbers
 * stay visible at a glance.
 */
const UtilizationCell = ({ util = {} }) => {
  const isPace = util.basis === 'pace_to_date';
  const weeklyTarget = Number(util.weekly_target_hours || 0);
  const weeklyHours = Number(util.weekly_hours || 0);
  const showWeeklyContext = isPace && weeklyTarget > 0;
  const paceTooltip = isPace
    ? `Pacing against ${Number(util.target_hours || 0).toFixed(1)}h expected by today (full week target ${weeklyTarget.toFixed(1)}h).`
    : '';
  return (
    <>
      <ProgressBar value={util.percent || 0} />
      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-secondary-500 dark:text-secondary-400">
        <span>
          {Number(util.week_hours || 0).toFixed(1)}h /{' '}
          {Number(util.target_hours || 0).toFixed(1)}h
        </span>
        {isPace && (
          <PaceBadge label={util.pace_label} tooltip={paceTooltip} />
        )}
      </div>
      {showWeeklyContext && (
        <div
          className="text-[10px] text-secondary-400 dark:text-secondary-500"
          data-testid="utilization-weekly-context"
        >
          {weeklyHours.toFixed(1)}h / {weeklyTarget.toFixed(1)}h this week
        </div>
      )}
    </>
  );
};

/**
 * Compact per-project chip row. Hides projects with 0 hours and lets the user
 * open a modal with the full list when there are more projects than fit.
 */
const PerProjectChips = ({ entries = [], onShowAll, user, rangeLabel = 'this week' }) => {
  const active = activeProjectEntries(entries);
  const rangeLower = (rangeLabel || 'this week').toLowerCase();
  if (!active.length) {
    return (
      <span className="text-xs text-secondary-500 dark:text-secondary-400">
        No tracked hours {rangeLower}
      </span>
    );
  }
  const visible = active.slice(0, CHIPS_VISIBLE);
  const overflow = active.length - visible.length;
  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((e) => (
        <span
          key={`${e.project_id}-${e.allocation_id || e.project_id}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-secondary-200 dark:border-secondary-600 text-[11px] text-secondary-700 dark:text-secondary-200 bg-secondary-50 dark:bg-secondary-800"
          title={`${e.project_name}: ${e.week_hours || 0}h (${rangeLower}) • ${
            e.is_billable ? 'Billable' : 'Non-billable'
          }${
            e.allocation_percent != null
              ? ` • planned ${e.allocation_percent}%`
              : ''
          }`}
        >
          <span className="truncate max-w-[120px]">{e.project_name}</span>
          {e.allocation_percent != null && (
            <span className="text-primary-700 dark:text-primary-300 font-semibold">
              {e.allocation_percent}%
            </span>
          )}
          <span className="text-secondary-500 dark:text-secondary-300">
            {Number(e.week_hours || 0).toFixed(1)}h
          </span>
        </span>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => onShowAll?.(user)}
          data-testid={`projects-more-${user?.id}`}
          className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50"
          title={`View all ${active.length} tracked projects`}
        >
          +{overflow} more
        </button>
      )}
    </div>
  );
};

/** Modal that lists every tracked project for a given employee. */
const ProjectsModal = ({ user, onClose, rangeLabel = 'This week' }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!user) return null;
  const entries = activeProjectEntries(user.per_project || []);
  const totalHours = entries.reduce(
    (sum, e) => sum + Number(e.week_hours || 0),
    0,
  );
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="projects-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 dark:bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl max-h-[80vh] overflow-hidden flex flex-col rounded-xl bg-white dark:bg-secondary-800 shadow-2xl border border-secondary-200 dark:border-secondary-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Projects ({(rangeLabel || 'This week').toLowerCase()})
            </div>
            <div className="text-base font-semibold text-secondary-900 dark:text-white truncate">
              {displayName(user)}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wide text-secondary-500">
                Total
              </div>
              <div className="text-sm font-semibold text-secondary-900 dark:text-white">
                {totalHours.toFixed(1)}h
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="projects-modal-close"
              className="p-1.5 rounded-md text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-700"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 divide-y divide-secondary-100 dark:divide-secondary-700">
          {entries.length === 0 ? (
            <div className="p-6 text-sm text-secondary-500">
              No tracked hours in this range.
            </div>
          ) : (
            entries.map((e) => (
              <div
                key={`${e.project_id}-${e.allocation_id || e.project_id}`}
                className="px-5 py-3 flex items-center gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                    {e.project_name}
                  </div>
                  {e.client ? (
                    <div className="text-xs text-secondary-500 truncate">
                      {e.client}
                    </div>
                  ) : null}
                </div>
                {e.allocation_percent != null && (
                  <span className="text-xs font-semibold text-primary-700 dark:text-primary-300 whitespace-nowrap">
                    {e.allocation_percent}% planned
                  </span>
                )}
                <span className="text-sm font-semibold text-secondary-900 dark:text-white whitespace-nowrap">
                  {Number(e.week_hours || 0).toFixed(1)}h
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const PeopleView = ({
  users = [],
  onEditUser,
  onManageAllocations,
  pageSize = PER_PAGE,
  rangeLabel = 'This week',
  visibleColumns,
}) => {
  const [page, setPage] = useState(1);
  const [modalUser, setModalUser] = useState(null);
  const show = (key) =>
    PEOPLE_COLUMNS.find((c) => c.key === key)?.locked ||
    visibleColumns?.[key] !== false;

  // Reset to the first page whenever the upstream user set changes so the
  // view never ends up stuck past the last page.
  useEffect(() => {
    setPage(1);
  }, [users]);

  const totalPages = Math.max(1, Math.ceil(users.length / pageSize));
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return users.slice(start, start + pageSize);
  }, [users, page, pageSize]);

  if (!users.length) {
    return (
      <Panel>
        <EmptyState
          icon={CalendarPlus}
          title="No resources match these filters"
          description="Adjust the filters above to see your team's utilization."
        />
      </Panel>
    );
  }

  return (
    <div className="space-y-4" data-testid="resource-people-view">
      <Panel className="overflow-hidden p-0">
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
            <thead className="bg-secondary-50 dark:bg-secondary-800/60">
              <tr>
                <th className={TH_CLASS}>Employee</th>
                {show('cohort') && <th className={TH_CLASS}>Cohort</th>}
                {show('utilization') && <th className={TH_CLASS}>Utilization</th>}
                {show('projects') && (
                  <th className={TH_CLASS} data-testid="projects-range-header">
                    Projects ({rangeLabel.toLowerCase()})
                  </th>
                )}
                {show('billability') && <th className={TH_CLASS}>Billability</th>}
                {show('comments') && <th className={TH_CLASS}>Comments</th>}
                {show('freeDate') && <th className={TH_CLASS}>Free date</th>}
                {show('actions') && (
                  <th className={`${TH_BASE} text-right`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100 dark:divide-secondary-800">
              {paged.map((user) => {
                const util = user.utilization || {};
                const freeIn = daysUntil(user.free_date);
                const hasBillable = isResourceBillable(user);
                const comment = (user.comments || '').trim();
                return (
                  <tr
                    key={user.id}
                    data-testid={`resource-row-${user.id}`}
                    className="hover:bg-secondary-50/60 dark:hover:bg-secondary-800/40 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar
                          blobName={user.avatar}
                          alt={displayName(user)}
                          size="md"
                          fallbackText={displayName(user)}
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-secondary-900 dark:text-white truncate">
                            {displayName(user)}
                          </div>
                          <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                            {user.department || user.role || 'No department'}
                          </div>
                          {(user.is_shared || user.is_over_allocated) && (
                            <div className="mt-1">
                              <ConflictChip
                                shared={user.is_shared}
                                over={user.is_over_allocated}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {show('cohort') && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        {user.cohort ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">
                            {user.cohort}
                          </span>
                        ) : (
                          <span className="text-xs text-secondary-400">-</span>
                        )}
                      </td>
                    )}
                    {show('utilization') && (
                      <td className="px-4 py-3 min-w-[180px]">
                        <UtilizationCell util={util} />
                      </td>
                    )}
                    {show('projects') && (
                      <td className="px-4 py-3">
                        <PerProjectChips
                          entries={user.per_project || []}
                          onShowAll={setModalUser}
                          user={user}
                          rangeLabel={rangeLabel}
                        />
                      </td>
                    )}
                    {show('billability') && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <BillableChip
                          billable={hasBillable}
                          manual={Boolean(user.billability)}
                        />
                      </td>
                    )}
                    {show('comments') && (
                      <td className="px-4 py-3 min-w-[160px] max-w-[260px]">
                        {comment ? (
                          <div
                            className="text-xs text-secondary-700 dark:text-secondary-200 line-clamp-2"
                            title={comment}
                          >
                            {comment}
                          </div>
                        ) : (
                          <span className="text-xs text-secondary-400">—</span>
                        )}
                      </td>
                    )}
                    {show('freeDate') && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {user.free_date ? (
                        <div>
                          <div className="text-secondary-900 dark:text-white">
                            {formatDate(user.free_date)}
                          </div>
                          {freeIn !== null && (
                            <div className="text-[11px] text-secondary-500 dark:text-secondary-400">
                              {freeIn <= 0
                                ? 'Free now'
                                : `in ${freeIn} day${freeIn === 1 ? '' : 's'}`}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-secondary-400">
                          No allocation
                        </span>
                      )}
                    </td>
                    )}
                    {show('actions') && (
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => onManageAllocations?.(user)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                          data-testid={`manage-allocations-${user.id}`}
                          title="Manage allocations"
                        >
                          <CalendarPlus className="h-3.5 w-3.5" />
                          Allocations
                        </button>
                        <button
                          type="button"
                          onClick={() => onEditUser?.(user)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700"
                          data-testid={`edit-user-${user.id}`}
                          title="Edit resource"
                        >
                          <Edit className="h-3.5 w-3.5" />
                          Edit
                        </button>
                      </div>
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-secondary-100 dark:divide-secondary-800">
          {paged.map((user) => {
            const util = user.utilization || {};
            const comment = (user.comments || '').trim();
            return (
              <div key={user.id} className="p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <Avatar
                    blobName={user.avatar}
                    alt={displayName(user)}
                    size="md"
                    fallbackText={displayName(user)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-secondary-900 dark:text-white truncate">
                      {displayName(user)}
                    </div>
                    <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                      {user.department || user.role || 'No department'}
                    </div>
                  </div>
                  <span
                    className={`text-xs font-semibold ${utilizationStatusTextClass(
                      util.status,
                    )}`}
                  >
                    {util.percent || 0}%
                  </span>
                </div>
                {show('utilization') && <UtilizationCell util={util} />}
                {show('projects') && (
                  <PerProjectChips
                    entries={user.per_project || []}
                    onShowAll={setModalUser}
                    user={user}
                    rangeLabel={rangeLabel}
                  />
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  {show('billability') && (
                    <BillableChip
                      billable={isResourceBillable(user)}
                      manual={Boolean(user.billability)}
                    />
                  )}
                  <ConflictChip
                    shared={user.is_shared}
                    over={user.is_over_allocated}
                  />
                  {show('cohort') && user.cohort && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">
                      {user.cohort}
                    </span>
                  )}
                </div>
                {show('comments') && comment && (
                  <div
                    className="text-xs text-secondary-700 dark:text-secondary-200 line-clamp-2"
                    title={comment}
                  >
                    {comment}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-secondary-500 dark:text-secondary-400">
                  <span>Free: {formatDate(user.free_date)}</span>
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onManageAllocations?.(user)}
                      className="px-2 py-1 rounded-md text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                    >
                      Allocations
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditUser?.(user)}
                      className="px-2 py-1 rounded-md text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      {totalPages > 1 && (
        <TablePagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
          totalItems={users.length}
          pageSize={pageSize}
          itemLabel="employees"
        />
      )}

      {modalUser && (
        <ProjectsModal
          user={modalUser}
          onClose={() => setModalUser(null)}
          rangeLabel={rangeLabel}
        />
      )}
    </div>
  );
};

export default PeopleView;
